import React, { Suspense, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import Header from "./components/Layout/Header";
import TourOverlay from "./components/Tour/TourOverlay";
import "./App.css";

// Lazy load the components
// Lazy loading is a technique that defers loading of non-critical resources at page load time.
// Instead of loading all components immediately, React.lazy() creates a dynamic import that
// only loads the component when it's actually needed. This improves initial page load performance
// by reducing the initial bundle size and only loading code when the user navigates to that route.
const Layout = React.lazy(() => import("./components/Layout/Layout"));
const WorkspaceProjects = React.lazy(() =>
  import("./components/Pages/WorkspaceProjects")
);
const Login = React.lazy(() => import("./components/UserAccount/Login"));
const AuthCallback = React.lazy(() =>
  import("./components/UserAccount/AuthCallback")
);
const Docking = React.lazy(() => import("./components/Docking/Docking"));
const Files = React.lazy(() => import("./components/FileManagement/Files"));
const MDScaleway = React.lazy(() =>
  import("./components/MDSimulation/MDScaleway")
);
const MDResults = React.lazy(() =>
  import("./components/MDSimulation/MDResults")
);
const DockingResults = React.lazy(() =>
  import("./components/Docking/DockingResults")
);
const UserProfile = React.lazy(() =>
  import("./components/UserAccount/UserProfile")
);
const UnderConstruction = React.lazy(() =>
  import("./components/Pages/UnderConstruction")
);
const Updating = React.lazy(() => import("./components/Pages/Updating"));
const BindingSitePrediction = React.lazy(() =>
  import("./components/Pages/BindingSitePrediction")
);
const Usage = React.lazy(() => import("./components/Billing/Usage"));
const Billing = React.lazy(() => import("./components/Billing/Billing"));
const Admin = React.lazy(() => import("./components/Admin/Admin"));
const MMPBSA = React.lazy(() => import("./components/Pages/MMPBSA"));
const ApiSettings = React.lazy(() => import("./components/Pages/ApiSettings"));
const PdbHealthReport = React.lazy(() =>
  import("./components/Pages/PdbHealthReport")
);
const ToolsHub = React.lazy(() => import("./components/Pages/ToolsHub"));
const ToolLanding = React.lazy(() => import("./components/Pages/ToolLanding"));

const App = () => {
  useEffect(() => {
    // Check if user has an old version and force refresh if needed
    const checkAppVersion = () => {
      // Only run version check in production
      if (
        process.env.NODE_ENV !== "production" ||
        !process.env.REACT_APP_BUILD_TIME
      ) {
        console.log("Skipping version check in development mode");
        return;
      }

      const storedVersion = localStorage.getItem("appVersion");

      // Use build time from environment variable or current time as fallback
      // const buildTime = process.env.REACT_APP_BUILD_TIME
      //   ? parseInt(process.env.REACT_APP_BUILD_TIME)
      //   : Date.now();

      const buildTime = parseInt(process.env.REACT_APP_BUILD_TIME);

      if (storedVersion) {
        const storedTime = parseInt(storedVersion);
        // If stored version is older than current build, force refresh
        if (storedTime < buildTime) {
          console.log("Old app version detected, forcing refresh...");
          console.log("Stored version:", new Date(storedTime).toISOString());
          console.log("Current build:", new Date(buildTime).toISOString());
          localStorage.setItem("appVersion", buildTime.toString());
          window.location.reload(true);
          return;
        }
      } else {
        // No version stored, set current version
        localStorage.setItem("appVersion", buildTime.toString());
        console.log("App version set to:", new Date(buildTime).toISOString());
      }
    };

    checkAppVersion();
  }, []);

  return (
    <div className="App">
      <Router>
        <Header />
        <TourOverlay />
        <Suspense fallback={<div>Loading...</div>}>
          <Routes>
            {/* Main routes */}
            <Route path="/" element={<WorkspaceProjects />} />
            <Route path="/login" element={<Login />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/under-construction" element={<UnderConstruction />} />
            <Route path="/report/:reportId" element={<PdbHealthReport />} />

            {/* Dashboard routes */}
            <Route path="/layout" element={<Navigate to="/layout/files" />} />

            {/* Workspace routes with Dashboard layout */}
            <Route path="/layout/:workspaceId/:projectId" element={<Layout />}>
              <Route path="files" element={<Files />} />
              <Route path="docking" element={<Docking />} />
              <Route path="simulation" element={<MDScaleway />} />
              <Route
                path="binding-site-prediction"
                element={<BindingSitePrediction />}
              />
              <Route path="analysis" element={<MDResults />} />
              <Route path="mmpbsa" element={<MMPBSA />} />
              <Route
                path="docking-analysis-dashboard"
                element={<DockingResults />}
              />
              <Route path="user-profile" element={<UserProfile />} />
              <Route path="usage" element={<Usage />} />
              <Route path="billing" element={<Billing />} />
              <Route path="api" element={<ApiSettings />} />
              <Route path="tools-hub" element={<ToolsHub />} />
              <Route path="tools/:toolSlug" element={<ToolLanding />} />
              <Route path="admin" element={<Admin />} />
            </Route>

            {/* Catch all route */}
            <Route path="*" element={<Updating />} />
          </Routes>
        </Suspense>
      </Router>

      {/* Stream Chat Widget */}
      {/* <StreamChatWidget /> */}
    </div>
  );
};

export default App;
